import { Collapse } from '@chakra-ui/react';
import React, { ReactNode } from 'react';
import { DropResult } from 'react-beautiful-dnd';

import {
  CmBox,
  CmCheckBox,
  CmDatePicker,
  CmFlex,
  CmFormControl,
  CmFormErrorMessage,
  CmFormLabel,
  CmHelpterText,
  CmLabelAndInput,
  CmLabelAndSelect,
  CmLabelAndText,
  CmLabelAndTextarea,
  CmLabelAndUpload,
  CmRadio,
  CmRadioGroup,
  CmSelect,
  CmStack,
  CmSwitch,
  CmText,
} from '@/components/uiParts';
import { PresignedUrl, unitChangeForByte } from '@/domain/models/Image';
import { OrderMethod, orderMethods } from '@/domain/models/Order/orderMethod';
import { TAX_RATE } from '@/domain/models/Order/pricePerTax';
import { IMAGE_RULE } from '@/pages/camel-order/sites/service';
import {
  MenuManagementDragItems,
  MenuManagementSearchAndDragArea,
} from '@/pages/menu-managements/components/SearchAndDrag';
import { MenuManagementCardWrapper } from '@/pages/menu-managements/components/Wrapper';
import {
  CanServeAlone,
  IsAuthRequired,
  Page,
  SalesPeriodType,
  SalesPeriodTypes,
} from '@/pages/menu-managements/items/hooks';
import {
  BrandItemDeliveryForm,
  BrandItemEatInForm,
  BrandItemTakeoutForm,
  itemForm,
} from '@/pages/menu-managements/items/service';
import { ItemFormError } from '@/pages/menu-managements/items/validate';
import { SearchItem } from '@/pages/menu-managements/services/searchItem';

type Props = {
  page: Page;
  titleJa: string;
  memo: string;
  image: PresignedUrl;
  description: string;
  salesStartAt: Date | null;
  salesEndAt: Date | null;
  takeout: BrandItemTakeoutForm;
  delivery: BrandItemDeliveryForm;
  eatIn: BrandItemEatInForm;
  isSearching: boolean;
  searchItems: SearchItem[];
  selectedItems: SearchItem[];
  searchItemName: string;
  limitOfPurchase: number | null;
  brandSelectValue: string;
  error: ItemFormError;
  canServeAlone: CanServeAlone;
  orderMethods: OrderMethod[];
  isAvailableEatin: boolean;
  handleInputName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeImageUrl: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClearImage: () => void;
  handleChangeCanServeAlone: (value: React.ReactText) => void;
  handleChangeOrderMethod: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputMemo: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleInputDescription: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleInputSalesStartAt: (date: Date | null) => void;
  handleInputSalesEndAt: (date: Date | null) => void;
  handleInputTakeoutPrice: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputTakeoutPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputTakeoutPosCodeCaseOptionGroupItem: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleInputTakeoutTax: (e: React.ReactText) => void;
  handleInputDeliveryPrice: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputDeliveryPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputDeliveryPosCodeCaseOptionGroupItem: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleInputDeliveryTax: (e: React.ReactText) => void;
  handleInputEatInPrice: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputEatInPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputEatInPosCodeCaseOptionGroupItem: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleInputEatInTax: (e: React.ReactText) => void;
  handleOnDragEnd: (result: DropResult) => void;
  handleSearchItemNameInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSelectItems: (item: SearchItem) => void;
  handleDeleteDragItem: (itemId: string) => void;
  isAuthRequired: IsAuthRequired;
  handleChangeLimitOfPurchase: (value: number | null) => void;
  handleChangeIsAuthRequired: (value: IsAuthRequired) => void;
  salesPeriodType: SalesPeriodType;
  handleSalesPeriodType: (value: SalesPeriodType) => void;
  handleSalesRelativeStartAt: (value: number) => void;
  handleSalesRelativeEndAt: (value: number) => void;
  salesRelativeStartAt: number;
  salesRelativeEndAt: number;
  // 商品に関する要望欄の設定
  specialInstructionsEnabled: boolean;
  specialInstructionsTitle: string;
  specialInstructionsPlaceholder: string;
  specialInstructionsRequired: boolean;
  handleChangeSpecialInstructionsEnabled: (enabled: boolean) => void;
  handleInputSpecialInstructionsTitle: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleInputSpecialInstructionsPlaceholder: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleChangeSpecialInstructionsRequired: (required: boolean) => void;
};

export const MenuManagementItemsForm: React.FC<Props> = ({
  page,
  titleJa,
  memo,
  image,
  description,
  salesStartAt,
  salesEndAt,
  takeout,
  delivery,
  eatIn,
  isSearching,
  searchItems,
  selectedItems,
  searchItemName,
  brandSelectValue,
  limitOfPurchase,
  error,
  canServeAlone,
  orderMethods: orderMethodInput,
  isAvailableEatin,
  handleInputName,
  handleChangeImageUrl,
  handleClearImage,
  handleInputMemo,
  handleInputDescription,
  handleInputSalesStartAt,
  handleInputSalesEndAt,
  handleInputTakeoutPrice,
  handleInputTakeoutPosCode,
  handleInputTakeoutPosCodeCaseOptionGroupItem,
  handleInputTakeoutTax,
  handleInputDeliveryPrice,
  handleInputDeliveryPosCode,
  handleInputDeliveryPosCodeCaseOptionGroupItem,
  handleInputDeliveryTax,
  handleInputEatInPrice,
  handleInputEatInPosCode,
  handleInputEatInPosCodeCaseOptionGroupItem,
  handleInputEatInTax,
  handleAddSelectItems,
  handleSearchItemNameInput,
  handleOnDragEnd,
  handleDeleteDragItem,
  handleChangeCanServeAlone,
  handleChangeOrderMethod,
  isAuthRequired,
  handleChangeIsAuthRequired,
  handleChangeLimitOfPurchase,
  salesPeriodType,
  handleSalesPeriodType,
  handleSalesRelativeStartAt,
  handleSalesRelativeEndAt,
  salesRelativeStartAt,
  salesRelativeEndAt,
  specialInstructionsEnabled,
  specialInstructionsTitle,
  specialInstructionsPlaceholder,
  specialInstructionsRequired,
  handleChangeSpecialInstructionsEnabled,
  handleInputSpecialInstructionsTitle,
  handleInputSpecialInstructionsPlaceholder,
  handleChangeSpecialInstructionsRequired,
}): JSX.Element => {
  const createRelativeDaysOptions = () => {
    const daysArray = [];
    for (let i = 0; i <= 365; i++) {
      daysArray.push({
        key: `${i}日後`,
        value: i,
      });
    }
    return daysArray;
  };

  const limitOfPurchaseOptions = [
    {
      label: '購入数制限なし',
      value: '',
    },
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((key: number) => ({
      label: `${key}点まで`,
      value: key,
    })),
  ];

  return (
    <MenuManagementCardWrapper>
      <CmStack spacing="20px">
        <CmBox>
          {page === 'new' && (
            <>
              <CmFormLabel isRequired fontWeight={'bold'}>
                商品を単品で販売可能にしますか
              </CmFormLabel>
              <CmRadioGroup
                defaultValue={canServeAlone}
                onChange={handleChangeCanServeAlone}
              >
                <CmStack direction="row">
                  <CmRadio size="md" value="1">
                    はい
                  </CmRadio>
                  <CmRadio size="md" value="0">
                    いいえ
                  </CmRadio>
                </CmStack>
              </CmRadioGroup>
            </>
          )}
          {page === 'edit' && (
            <CmLabelAndText
              label="販売種別"
              name={canServeAlone === '1' ? '単品で販売可能' : '単品で販売不可'}
            />
          )}
        </CmBox>

        <CmBox>
          <CmFormLabel isRequired fontWeight="bold">
            {itemForm.orderMethod.label}
          </CmFormLabel>
          <CmBox pl="20px">
            <CmHelpterText mb="5px">※最低一つを選択して下さい。</CmHelpterText>
            <CmStack spacing="15px">
              {orderMethods.map((option, index) => (
                <CmCheckBox
                  key={index}
                  ml={index > 0 ? '20px' : undefined}
                  isChecked={orderMethodInput.includes(option.value)}
                  value={option.value}
                  onChange={handleChangeOrderMethod}
                  isDisabled={!isAvailableEatin && option.value === 'EAT_IN'}
                >
                  {option.name}
                </CmCheckBox>
              ))}
            </CmStack>
          </CmBox>
        </CmBox>
        <CmLabelAndInput
          fontWeight={'bold'}
          isRequired
          id={itemForm.titleJa.key}
          isInvalid={error.titleJa.isError}
          label={'商品名'}
          value={titleJa}
          onChange={handleInputName}
        >
          <CmFormErrorMessage>{error.titleJa.message}</CmFormErrorMessage>
        </CmLabelAndInput>

        <>
          <CmFormLabel isRequired fontWeight={'bold'}>
            会員用の商品ですか？
          </CmFormLabel>
          <CmRadioGroup onChange={handleChangeIsAuthRequired}>
            <CmStack direction="row">
              <CmRadio size="md" value="0" isChecked={isAuthRequired === '0'}>
                全てのお客様
              </CmRadio>
              <CmRadio size="md" value="1" isChecked={isAuthRequired === '1'}>
                会員のみ
              </CmRadio>
            </CmStack>
          </CmRadioGroup>
        </>

        <CmFormLabel isRequired fontWeight={'bold'}>
          1注文あたりの購入上限数
        </CmFormLabel>
        <CmSelect
          rootProps={{ flex: 1, mx: 4, fontWeight: 'bold' }}
          value={limitOfPurchase ?? undefined}
          onChange={(e) => {
            const strVal = e.target.value;
            if (strVal === '') {
              handleChangeLimitOfPurchase(null);
              return;
            }
            const numberVal = Number(strVal);
            if (isNaN(numberVal)) {
              handleChangeLimitOfPurchase(null);
              return;
            }
            handleChangeLimitOfPurchase(numberVal);
          }}
          borderColor="gray.300"
        >
          {limitOfPurchaseOptions.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </CmSelect>

        <CmLabelAndUpload
          fontWeight={'bold'}
          id={itemForm.image.key}
          label={itemForm.image.label}
          src={image.image_url}
          accept={IMAGE_RULE.ITEM_IMAGE.allowedImageTypes.join(',')}
          onChange={handleChangeImageUrl}
          onClear={handleClearImage}
        >
          <CmHelpterText mt="3px">
            ファイルの要件:
            {IMAGE_RULE.ITEM_IMAGE.allowedImageFormats.join('、')} (最大
            {unitChangeForByte(IMAGE_RULE.ITEM_IMAGE.maxSize)}) <br />
            最小ピクセル数: 横幅を {IMAGE_RULE.ITEM_IMAGE.minWidth}
            以上、高さを
            {IMAGE_RULE.ITEM_IMAGE.minHeight}以上にしてください。
          </CmHelpterText>
        </CmLabelAndUpload>

        <CmLabelAndTextarea
          fontWeight={'bold'}
          id={itemForm.memo.key}
          isInvalid={error.memo.isError}
          value={memo}
          label={itemForm.memo.label}
          error={<CmFormErrorMessage>{error.memo.message}</CmFormErrorMessage>}
          onChange={handleInputMemo}
        />

        <CmLabelAndTextarea
          fontWeight={'bold'}
          id={itemForm.description.key}
          isInvalid={error.description.isError}
          value={description}
          label={itemForm.description.label}
          error={
            <CmFormErrorMessage>{error.description.message}</CmFormErrorMessage>
          }
          onChange={handleInputDescription}
        />
        <>
          <CmFormLabel isRequired fontWeight={'bold'}>
            受け取り可能期間
          </CmFormLabel>
          <CmRadioGroup onChange={handleSalesPeriodType}>
            <CmStack direction="row">
              <CmRadio
                size="md"
                value={SalesPeriodTypes.NONE}
                isChecked={salesPeriodType === SalesPeriodTypes.NONE}
              >
                設定しない
              </CmRadio>
              <CmRadio
                size="md"
                value={SalesPeriodTypes.ABSOLUTE}
                isChecked={salesPeriodType === SalesPeriodTypes.ABSOLUTE}
              >
                日時を指定
              </CmRadio>
              <CmRadio
                size="md"
                value={SalesPeriodTypes.RELATIVE}
                isChecked={salesPeriodType === SalesPeriodTypes.RELATIVE}
              >
                相対日付で指定
              </CmRadio>
            </CmStack>
          </CmRadioGroup>
        </>
        {salesPeriodType === SalesPeriodTypes.ABSOLUTE && (
          <DateTimeRangeInput
            label=""
            startDate={salesStartAt}
            endDate={salesEndAt}
            handleChangeStartDate={handleInputSalesStartAt}
            handleChangeEndDate={handleInputSalesEndAt}
            isInvalid={error.salesPeriod.isError}
            error={
              <CmFormErrorMessage>
                {error.salesPeriod.message}
              </CmFormErrorMessage>
            }
          />
        )}
        {salesPeriodType === SalesPeriodTypes.RELATIVE && (
          <CmFormControl isInvalid={true}>
            <CmFlex direction="row" justifyContent="flex-start" align="center">
              <CmLabelAndSelect
                label=""
                width="300px"
                fontWeight="bold"
                value={salesRelativeStartAt}
                onChange={(e) => {
                  handleSalesRelativeStartAt(Number(e.target.value));
                }}
              >
                {createRelativeDaysOptions().map((relativeDay) => (
                  <option key={relativeDay.value} value={relativeDay.value}>
                    {relativeDay.key}
                  </option>
                ))}
              </CmLabelAndSelect>
              <CmText mx={2}>〜</CmText>
              <CmLabelAndSelect
                label=""
                width="300px"
                fontWeight="bold"
                value={salesRelativeEndAt}
                onChange={(e) => {
                  handleSalesRelativeEndAt(Number(e.target.value));
                }}
              >
                {createRelativeDaysOptions().map((relativeDay) => (
                  <option key={relativeDay.value} value={relativeDay.value}>
                    {relativeDay.key}
                  </option>
                ))}
              </CmLabelAndSelect>
            </CmFlex>
            <CmFormErrorMessage>
              {error.relativeSalesPeriod.message}
            </CmFormErrorMessage>
          </CmFormControl>
        )}

        {orderMethodInput.includes('TAKE_OUT') && (
          <>
            <CmLabelAndInput
              isRequired
              isInvalid={error.takeoutPrice.isError}
              fontWeight={'bold'}
              id={itemForm.takeout.price.key}
              label={itemForm.takeout.price.label}
              value={takeout.price}
              onChange={handleInputTakeoutPrice}
            >
              <CmFormErrorMessage>
                {error.takeoutPrice.message}
              </CmFormErrorMessage>
            </CmLabelAndInput>

            <CmBox>
              <CmFormLabel
                htmlFor={itemForm.takeout.tax.key}
                isRequired
                fontWeight={'bold'}
              >
                {itemForm.takeout.tax.label}
              </CmFormLabel>
              <CmRadioGroup
                id={itemForm.takeout.tax.key}
                value={takeout.tax_rate.toString()}
                onChange={handleInputTakeoutTax}
              >
                <CmStack direction="row">
                  {Object.values(TAX_RATE).map((taxRate) => (
                    <CmRadio size="md" key={taxRate} value={taxRate.toString()}>
                      {taxRate}%
                    </CmRadio>
                  ))}
                </CmStack>
              </CmRadioGroup>
            </CmBox>
            <CmLabelAndInput
              fontWeight={'bold'}
              id={itemForm.takeout.pos_code.key}
              label={itemForm.takeout.pos_code.label}
              value={takeout.pos_code}
              onChange={handleInputTakeoutPosCode}
            >
              <CmHelpterText mt="3px">
                テイクアウト注文、かつ商品が親商品で注文された際はこのコードが連携されます。
              </CmHelpterText>
            </CmLabelAndInput>

            <CmLabelAndInput
              fontWeight={'bold'}
              id={itemForm.takeout.pos_code_case_option_group_item.key}
              label={itemForm.takeout.pos_code_case_option_group_item.label}
              value={takeout.pos_code_case_option_group_item}
              onChange={handleInputTakeoutPosCodeCaseOptionGroupItem}
            >
              <CmHelpterText mt="3px">
                テイクアウト注文、かつ商品がオプション商品で注文された際はこのコードが連携されます。設定されていない場合は親商品に設定されたPOSコードが連携されます。
              </CmHelpterText>
            </CmLabelAndInput>
          </>
        )}
        {orderMethodInput.includes('EAT_IN') && (
          <>
            <CmLabelAndInput
              isRequired
              isInvalid={error.eatInPrice.isError}
              fontWeight={'bold'}
              id={itemForm.eatIn.price.key}
              label={itemForm.eatIn.price.label}
              value={eatIn.price}
              onChange={handleInputEatInPrice}
            >
              <CmFormErrorMessage>
                {error.eatInPrice.message}
              </CmFormErrorMessage>
            </CmLabelAndInput>

            <CmBox>
              <CmFormLabel
                htmlFor={itemForm.eatIn.tax.key}
                isRequired
                fontWeight={'bold'}
              >
                {itemForm.eatIn.tax.label}
              </CmFormLabel>
              <CmRadioGroup
                id={itemForm.eatIn.tax.key}
                value={eatIn.tax_rate.toString()}
                onChange={handleInputEatInTax}
              >
                <CmStack direction="row">
                  {Object.values(TAX_RATE).map((taxRate) => (
                    <CmRadio size="md" key={taxRate} value={taxRate.toString()}>
                      {taxRate}%
                    </CmRadio>
                  ))}
                </CmStack>
              </CmRadioGroup>
            </CmBox>
            <CmLabelAndInput
              fontWeight={'bold'}
              id={itemForm.eatIn.pos_code.key}
              label={itemForm.eatIn.pos_code.label}
              value={eatIn.pos_code}
              onChange={handleInputEatInPosCode}
            >
              <CmHelpterText mt="3px">
                店内飲食注文、かつ商品が親商品で注文された際はこのコードが連携されます。
              </CmHelpterText>
            </CmLabelAndInput>

            <CmLabelAndInput
              fontWeight={'bold'}
              id={itemForm.eatIn.pos_code_case_option_group_item.key}
              label={itemForm.eatIn.pos_code_case_option_group_item.label}
              value={eatIn.pos_code_case_option_group_item}
              onChange={handleInputEatInPosCodeCaseOptionGroupItem}
            >
              <CmHelpterText mt="3px">
                店内飲食注文、かつ商品がオプション商品で注文された際はこのコードが連携されます。設定されていない場合は親商品に設定されたPOSコードが連携されます。
              </CmHelpterText>
            </CmLabelAndInput>
          </>
        )}
        {orderMethodInput.includes('DELIVERY') && (
          <>
            <CmLabelAndInput
              isRequired
              isInvalid={error.deliveryPrice.isError}
              fontWeight={'bold'}
              id={itemForm.delivery.price.key}
              label={itemForm.delivery.price.label}
              value={delivery.price}
              onChange={handleInputDeliveryPrice}
            >
              <CmFormErrorMessage>
                {error.deliveryPrice.message}
              </CmFormErrorMessage>
            </CmLabelAndInput>

            <CmBox>
              <CmFormLabel
                htmlFor={itemForm.delivery.tax.key}
                isRequired
                fontWeight={'bold'}
              >
                {itemForm.delivery.tax.label}
              </CmFormLabel>
              <CmRadioGroup
                id={itemForm.delivery.tax.key}
                value={delivery.tax_rate.toString()}
                onChange={handleInputDeliveryTax}
              >
                <CmStack direction="row">
                  {Object.values(TAX_RATE).map((taxRate) => (
                    <CmRadio size="md" key={taxRate} value={taxRate.toString()}>
                      {taxRate}%
                    </CmRadio>
                  ))}
                </CmStack>
              </CmRadioGroup>
            </CmBox>
            <CmLabelAndInput
              fontWeight={'bold'}
              id={itemForm.delivery.pos_code.key}
              label={itemForm.delivery.pos_code.label}
              value={delivery.pos_code}
              onChange={handleInputDeliveryPosCode}
            >
              <CmHelpterText mt="3px">
                デリバリー注文、かつ商品が親商品で注文された際はこのコードが連携されます。
              </CmHelpterText>
            </CmLabelAndInput>

            <CmLabelAndInput
              fontWeight={'bold'}
              id={itemForm.delivery.pos_code_case_option_group_item.key}
              label={itemForm.delivery.pos_code_case_option_group_item.label}
              value={delivery.pos_code_case_option_group_item}
              onChange={handleInputDeliveryPosCodeCaseOptionGroupItem}
            >
              <CmHelpterText mt="3px">
                デリバリー注文、かつ商品がオプション商品で注文された際はこのコードが連携されます。設定されていない場合は親商品に設定されたPOSコードが連携されます。
              </CmHelpterText>
            </CmLabelAndInput>
          </>
        )}

        {/* 商品に関する要望欄の設定 */}
        <CmBox>
          <CmFlex alignItems="center" mb="10px">
            <CmFormLabel fontWeight="bold" mb="0">
              商品に関する要望欄
            </CmFormLabel>
            <CmSwitch
              ml="10px"
              isChecked={specialInstructionsEnabled}
              onChange={(e) =>
                handleChangeSpecialInstructionsEnabled(e.target.checked)
              }
            />
          </CmFlex>
          <Collapse in={specialInstructionsEnabled} animateOpacity>
            <CmStack spacing="15px" pl="20px">
              <CmBox>
                <CmFormLabel fontWeight="bold" isRequired>
                  任意・必須
                </CmFormLabel>
                <CmRadioGroup
                  value={specialInstructionsRequired ? '1' : '0'}
                  onChange={(value: string) => {
                    handleChangeSpecialInstructionsRequired(value === '1');
                  }}
                >
                  <CmStack direction="row">
                    <CmRadio size="md" value={'0'}>
                      任意
                    </CmRadio>
                    <CmRadio size="md" value={'1'}>
                      必須
                    </CmRadio>
                  </CmStack>
                </CmRadioGroup>
              </CmBox>
              <CmBox mt="5px">
                <CmLabelAndInput
                  fontWeight="bold"
                  isRequired
                  isInvalid={error.specialInstructionsTitle?.isError}
                  id="specialInstructionsTitle"
                  label="項目名"
                  value={specialInstructionsTitle}
                  onChange={handleInputSpecialInstructionsTitle}
                >
                  <CmFormErrorMessage>
                    {error.specialInstructionsTitle?.message}
                  </CmFormErrorMessage>
                </CmLabelAndInput>
                <CmHelpterText mt="3px">80字まで</CmHelpterText>
              </CmBox>
              <CmBox mt="5px">
                <CmLabelAndInput
                  fontWeight="bold"
                  isInvalid={error.specialInstructionsPlaceholder?.isError}
                  id="specialInstructionsPlaceholder"
                  label="説明文（入力欄に表示される文言）"
                  value={specialInstructionsPlaceholder}
                  onChange={handleInputSpecialInstructionsPlaceholder}
                >
                  <CmFormErrorMessage>
                    {error.specialInstructionsPlaceholder?.message}
                  </CmFormErrorMessage>
                </CmLabelAndInput>
                <CmHelpterText mt="3px">200字まで</CmHelpterText>
              </CmBox>
            </CmStack>
          </Collapse>
        </CmBox>

        <MenuManagementSearchAndDragArea
          targetName="紐付けオプショングループ"
          {...{
            brandSelectValue,
            searchItems,
            isSearching,
            selectedItems,
            searchItemName,
            handleSearchItemNameInput,
            handleAddSelectItems,
          }}
        >
          <MenuManagementDragItems
            targetOption="option-groups"
            {...{ selectedItems, handleOnDragEnd, handleDeleteDragItem }}
          />
        </MenuManagementSearchAndDragArea>
      </CmStack>
    </MenuManagementCardWrapper>
  );
};

type DateTimeRangeInputProps = {
  label: string;
  startDate: Date | null;
  endDate: Date | null;
  handleChangeStartDate: (date: Date | null) => void;
  handleChangeEndDate: (date: Date | null) => void;
  isInvalid: boolean;
  error?: ReactNode;
};

const DateTimeRangeInput: React.FC<DateTimeRangeInputProps> = ({
  label,
  startDate,
  endDate,
  handleChangeStartDate,
  handleChangeEndDate,
  isInvalid,
  error,
}): JSX.Element => {
  return (
    <CmFormControl isInvalid={isInvalid}>
      <CmFormLabel fontWeight="bold">{label}</CmFormLabel>
      <CmFlex direction="row" justifyContent="flex-start" align="center">
        <DateTimeInput
          date={startDate}
          handleChangeDate={handleChangeStartDate}
        />
        <CmText mx={2}>〜</CmText>
        <DateTimeInput date={endDate} handleChangeDate={handleChangeEndDate} />
      </CmFlex>
      {error}
    </CmFormControl>
  );
};

type DateTimeInputProps = {
  date: Date | null;
  handleChangeDate: (date: Date | null) => void;
};

const DateTimeInput: React.FC<DateTimeInputProps> = ({
  date,
  handleChangeDate,
}): JSX.Element => {
  return (
    <CmFlex direction="row" justifyContent="flex-start" alignItems="center">
      <CmDatePicker
        selected={date}
        onChange={handleChangeDate}
        dateFormat="yyyy-MM-dd HH:mm"
        showTimeSelect
      />
    </CmFlex>
  );
};
